import type {
  AnswerSpec,
  BilanPage,
  ClassChapterList,
  ClassChapterOption,
  ClassNow,
  ConsentRecord,
  ConsentsView,
  EvalReviewView,
  FrLevel,
  GoalsUpdate,
  GoalsView,
  HintLevel,
  ItemSolution,
  Locale,
  ParentGoals,
  ParentPrefs,
  ProgressReport,
  ReportTotals,
  SessionBilan,
  SessionBilans,
  SessionClimb,
  SessionGoal,
  SessionNotes,
  SessionPages,
  SkillProgressRow,
  Subject,
  Urgency,
  WidgetBlock,
} from "@tutor/protocol";
import { z } from "zod";
import type { ApiError } from "../auth/api";

// Parent app API calls (workstream 7 routes in apps/api/src/parent-app.ts). Same origin,
// cookie session, responses validated against schemas tied to the generated wire types.

export type { ApiError } from "../auth/api";

const errorBody = z.looseObject({ error: z.string().optional(), code: z.string().optional() });

/** A failed call, plus the parsed body when the API sent one (a 409 carries `current`). */
export type ParentResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ApiError; readonly body: unknown };

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown; device?: string },
  schema: z.ZodType<T>,
): Promise<ParentResult<T>> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  if (init.device !== undefined) headers["authorization"] = `Device ${init.device}`;
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      credentials: "include",
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    return { ok: false, error: { kind: "offline" }, body: null };
  }
  const json: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.status === 429) return { ok: false, error: { kind: "rate_limited" }, body: json };
  if (!res.ok) {
    const body = errorBody.safeParse(json);
    const code = body.success ? (body.data.error ?? body.data.code) : undefined;
    return { ok: false, error: { kind: "http", status: res.status, code }, body: json };
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      error: { kind: "http", status: res.status, code: "invalid_response" },
      body: json,
    };
  }
  return { ok: true, value: parsed.data };
}

const profilePath = (profileId: string, rest: string) =>
  `/v1/profiles/${encodeURIComponent(profileId)}/${rest}`;

const subject = z.enum([
  "math",
  "reasoning",
  "sciences",
  "humanities",
]) satisfies z.ZodType<Subject>;
export const SUBJECTS: readonly Subject[] = subject.options;

const goals = z.object({
  allowedSubjects: z.array(subject),
  dailyMinutesCap: z.number(),
  quietStart: z.number(),
  quietEnd: z.number(),
  maxDifficulty: z.number(),
  askParentOnFrustration: z.boolean(),
}) satisfies z.ZodType<ParentGoals>;

const prefs = z.object({
  notifyFrustrationNow: z.boolean(),
  progressDigest: z.boolean(),
  weeklyEmail: z.boolean(),
  freeQuestions: z.boolean().nullable().exactOptional(),
  oneBoard: z.boolean().nullable().exactOptional(),
}) satisfies z.ZodType<ParentPrefs>;

const frLevel = z.enum(["CM1", "CM2", "6e", "5e", "4e", "3e"]) satisfies z.ZodType<FrLevel>;
/** The classes a parent can choose, CM1 to 3e. */
export const FR_LEVELS: readonly FrLevel[] = frLevel.options;

const classNow = z.object({
  level: frLevel,
  section: z.string().nullable().exactOptional(),
  testOn: z.string().nullable().exactOptional(),
  chosenBy: z.enum(["parent", "child"]).nullable().exactOptional(),
}) satisfies z.ZodType<ClassNow>;

export const goalsView = z.object({
  goals,
  prefs,
  classNow: classNow.nullable().exactOptional(),
  version: z.number(),
}) satisfies z.ZodType<GoalsView>;

const chapterOption = z.object({
  code: z.string(),
  subject: z.string(),
  theme: z.string(),
  topic: z.string(),
}) satisfies z.ZodType<ClassChapterOption>;

const chapterList = z.object({
  level: frLevel,
  chapters: z.array(chapterOption),
}) satisfies z.ZodType<ClassChapterList>;

const skillRow = z.object({
  skillId: z.string(),
  mastery: z.number(),
  masteryChange: z.number(),
  attempts: z.number(),
  nextReviewDay: z.number().nullable(),
  climb: z.object({ step: z.number(), steps: z.number() }).nullable().exactOptional(),
}) satisfies z.ZodType<SkillProgressRow>;

const totals = z.object({
  attempts: z.number(),
  activeDays: z.number(),
}) satisfies z.ZodType<ReportTotals>;

const report = z.object({
  profileId: z.string(),
  days: z.number(),
  from: z.string(),
  to: z.string(),
  skills: z.array(skillRow),
  totals,
}) satisfies z.ZodType<ProgressReport>;

const sessionGoal = z.object({
  skillId: z.string(),
  kind: z.enum(["climb", "review"]),
  target: z.number(),
  state: z.enum(["todo", "done", "setAside"]),
}) satisfies z.ZodType<SessionGoal>;

const sessionClimb = z.object({
  skillId: z.string(),
  fromStep: z.number(),
  toStep: z.number(),
  steps: z.number(),
  mastered: z.boolean(),
}) satisfies z.ZodType<SessionClimb>;

const sessionNotes = z.object({
  strong: z.string().nullable().exactOptional(),
  watch: z.string().nullable().exactOptional(),
  question: z.string().nullable().exactOptional(),
}) satisfies z.ZodType<SessionNotes>;

const sessionBilan = z.object({
  sessionStart: z.number(),
  lastAt: z.string(),
  minutes: z.number().nullable().exactOptional(),
  goals: z.array(sessionGoal),
  exercises: z.number(),
  firstTry: z.number(),
  withHints: z.number(),
  climbs: z.array(sessionClimb),
  notes: sessionNotes.nullable().exactOptional(),
}) satisfies z.ZodType<SessionBilan>;

const sessionBilans = z.object({
  profileId: z.string(),
  days: z.number(),
  sessions: z.array(sessionBilan),
  notesPending: z.boolean(),
}) satisfies z.ZodType<SessionBilans>;

const answerSpec = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("choice"), choices: z.array(z.string()) }),
  z.object({ kind: z.literal("number"), unit: z.string().nullable() }),
  z.object({ kind: z.literal("text"), maxLength: z.number() }),
]) satisfies z.ZodType<AnswerSpec>;

const solution = z.object({
  answer: z.string(),
  explanation: z.string(),
}) satisfies z.ZodType<ItemSolution>;

const widget = z.object({
  library: z.string(),
  source: z.string(),
}) satisfies z.ZodType<WidgetBlock>;

const hintLevel = z.enum([
  "socratic",
  "nudge",
  "strategy",
  "partialStep",
  "workedExample",
]) satisfies z.ZodType<HintLevel>;

const bilanPage = z.object({
  itemId: z.string(),
  skillId: z.string(),
  prompt: z.string(),
  answer: answerSpec,
  tries: z.array(z.boolean()),
  solution: solution.nullable().exactOptional(),
  bench: widget.nullable().exactOptional(),
  hint: hintLevel.nullable().exactOptional(),
}) satisfies z.ZodType<BilanPage>;

const sessionPages = z.object({
  sessionStart: z.number(),
  pages: z.array(bilanPage),
}) satisfies z.ZodType<SessionPages>;

const consentRecord = z.object({
  noticeVersion: z.string(),
  method: z.string(),
  grantedAt: z.string(),
  current: z.boolean(),
}) satisfies z.ZodType<ConsentRecord>;

const consents = z.object({
  consents: z.array(consentRecord),
  currentVersion: z.string(),
  upToDate: z.boolean(),
}) satisfies z.ZodType<ConsentsView>;

const evalReview = z.object({
  allowed: z.boolean(),
  objectedAt: z.string().nullable(),
}) satisfies z.ZodType<EvalReviewView>;

const urgency = z.enum(["none", "digest", "now"]) satisfies z.ZodType<Urgency>;

/** The `parent_alerts` reasons as GET /v1/alerts returns them (snake_case column values). */
export const ALERT_REASONS = [
  "safety_low",
  "safety_high",
  "frustration",
  "plateau",
  "daily_cap",
] as const;
export type AlertReason = (typeof ALERT_REASONS)[number];

const alert = z.object({
  id: z.string(),
  profileId: z.string(),
  urgency,
  reason: z.enum(ALERT_REASONS),
  skillId: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type ParentAlertRow = z.infer<typeof alert>;

export function getReport(profileId: string, days = 7) {
  return request(profilePath(profileId, `report?days=${String(days)}`), {}, report);
}

/**
 * The bilan of each session of the last `days` days, newest first (ADR-0055), with the
 * written lines in the parent's `locale`.
 */
export function getSessions(profileId: string, locale: Locale, days = 7) {
  return request(
    profilePath(profileId, `sessions?days=${String(days)}&locale=${locale}`),
    {},
    sessionBilans,
  );
}

/** One session's board pages, oldest first. */
export function getSessionPages(profileId: string, sessionStart: number) {
  return request(
    profilePath(profileId, `sessions/${String(sessionStart)}/pages`),
    {},
    sessionPages,
  );
}

export function getGoals(profileId: string) {
  return request(profilePath(profileId, "goals"), {}, goalsView);
}

/** The chapters a parent can pick for a classe (ADR-0053). */
export function getClassChapters(level: FrLevel) {
  return request(`/v1/class-chapters/${encodeURIComponent(level)}`, {}, chapterList);
}

export function putGoals(profileId: string, update: GoalsUpdate) {
  return request(profilePath(profileId, "goals"), { method: "PUT", body: update }, goalsView);
}

/** The latest goals a 409 `version_conflict` carries, if it carries valid ones. */
export function conflictCurrent(body: unknown): GoalsView | undefined {
  const parsed = z.looseObject({ current: goalsView }).safeParse(body);
  return parsed.success ? parsed.data.current : undefined;
}

export function getConsents(profileId: string) {
  return request(profilePath(profileId, "consents"), {}, consents);
}

export function acceptNotice(profileId: string, noticeVersion: string) {
  return request(
    profilePath(profileId, "consents"),
    { method: "POST", body: { noticeVersion } },
    z.unknown(),
  );
}

/** The household's choice on conversation review (ADR-0050). */
export function getEvalReview() {
  return request("/v1/household/eval-review", {}, evalReview);
}

/** Either way needs a fresh email code, like consent (ADR-0050, ADR-0048). */
export function putEvalReview(allowed: boolean) {
  return request("/v1/household/eval-review", { method: "PUT", body: { allowed } }, evalReview);
}

/**
 * Lifts the child's safety pause (ADR-0022). Needs a fresh email code; `resumed` is false
 * when no pause was in force any more.
 */
export function resumeSession(profileId: string) {
  return request(
    profilePath(profileId, "resume"),
    { method: "POST" },
    z.object({ resumed: z.boolean() }),
  );
}

export function getAlerts() {
  return request("/v1/alerts", {}, z.object({ alerts: z.array(alert) }));
}

export function markAlertRead(alertId: string) {
  return request(`/v1/alerts/${encodeURIComponent(alertId)}/read`, { method: "POST" }, z.unknown());
}

// Parents remembered on this device (ADR-0048). `device` is the device credential.

const deviceGrant = z.object({ id: z.string(), parent: z.string() });
export type DeviceGrant = z.infer<typeof deviceGrant>;

/** The parents remembered on this device, with masked emails, for the PIN screen. */
export function getDeviceGrants(device: string) {
  return request(
    "/v1/device/parent-grants",
    { device },
    z.object({ grants: z.array(deviceGrant) }),
  );
}

/** Opens parent mode with the parent PIN; errors: wrong_pin, locked, not_found. */
export function unlockWithPin(device: string, grantId: string, pin: string) {
  return request(
    "/v1/parent-unlock",
    { method: "POST", body: { grantId, pin }, device },
    z.object({ expiresAt: z.string() }),
  );
}

/** Ends a PIN unlock; harmless without one. */
export function endUnlock() {
  return request("/v1/parent-unlock/end", { method: "POST" }, z.unknown());
}

/** Remembers the signed-in parent on this device. Needs a fresh email code. */
export function rememberDevice(device: string, pin: string) {
  return request(
    "/v1/parent-grants",
    { method: "POST", body: { pin }, device },
    z.object({ id: z.string(), expiresAt: z.string() }),
  );
}

const grant = z.object({
  id: z.string(),
  deviceId: z.string(),
  deviceLabel: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  lastUnlockedAt: z.string().nullable(),
});
export type ParentGrant = z.infer<typeof grant>;

/** The devices where the signed-in parent stays signed in. */
export function listGrants() {
  return request("/v1/parent-grants", {}, z.object({ grants: z.array(grant) }));
}

/**
 * « Se déconnecter partout »: ends every session of this parent and forgets every device
 * that remembers them (ADR-0048). Better Auth asks for a recent email code.
 */
export function signOutEverywhere() {
  return request("/api/auth/revoke-sessions", { method: "POST", body: {} }, z.unknown());
}

export function revokeGrant(grantId: string) {
  return request(
    `/v1/parent-grants/${encodeURIComponent(grantId)}`,
    { method: "DELETE" },
    z.unknown(),
  );
}
